// Hierarchy (SM-712): the selected node's subtree as an expandable tree
// over GET /{id}/children (each row knows its child_count, so only rows
// with children expand), paginated per level, with search over
// GET /{id}/descendants?q= and the breadcrumb from GET /{id}/path (which
// never reaches above the caller's covering membership). Every row can
// become the selected node. Effective status is shown beside the node's
// own, because a PAUSED ancestor restricts an ACTIVE child.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useCan, useScope } from "@/components/layout/ScopeContext";
import { useSelectNode } from "@/components/layout/NodeSelector";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { EmptyState, ErrorState, LoadingState, QueryBlock } from "@/components/ui/States";
import { Select } from "@/components/ui/Field";
import { CreateChildDialog } from "@/components/CreateChildDialog";
import { fetchChildren, fetchDescendants, keys } from "@/lib/queries";
import { brokerStatusTone, restrictedByAncestor } from "@/lib/status";
import { BROKER_STATUSES, type BrokerItem } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";

export const Route = createFileRoute("/_app/hierarchy")({
  beforeLoad: capabilityGuard("VIEW"),
  component: HierarchyPage,
});

const LEVEL_LIMIT = 20;

function HierarchyPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const scope = useScope();
  const canCreate = useCan("CHILD_CREATE");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [creating, setCreating] = useState(false);
  const firstPage = { status: status || undefined, offset: 0, limit: LEVEL_LIMIT };
  const probe = { queryKey: keys.children(scope.nodeId, firstPage), queryFn: () => fetchChildren(scope.nodeId, firstPage) };

  const searchQ = useQuery({
    queryKey: keys.descendants(scope.nodeId, { q: submitted, include_self: false, limit: 50 }),
    queryFn: () => fetchDescendants(scope.nodeId, { q: submitted, include_self: false, limit: 50 }),
    enabled: guard.kind === "allow" && submitted !== "",
  });

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader
        title={t("hierarchy.title")}
        subtitle={t("hierarchy.subtitle")}
        coverage="subtree"
        actions={canCreate && <Button onClick={() => setCreating(true)}>{t("child.open")}</Button>}
      />
      <nav aria-label={t("node.breadcrumb")} className="mb-4">
        <ol className="flex flex-wrap items-center gap-1 text-xs text-t-3">
          {(scope.path.path ?? []).map((c, i) => (
            <li key={c.broker_id} className="flex items-center gap-1">
              {i > 0 && <span aria-hidden>›</span>}
              <BreadcrumbLink id={c.broker_id} name={c.display_name} current={c.broker_id === scope.nodeId} />
            </li>
          ))}
        </ol>
      </nav>

      <SectionCard title={t("hierarchy.search")} className="mb-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(q.trim());
          }}
        >
          <div className="min-w-[14rem] flex-1">
            <label htmlFor="hier-q" className="text-2xs font-semibold uppercase tracking-wide text-t-3">
              {t("hierarchy.searchLabel")}
            </label>
            <input
              id="hier-q"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("node.searchPlaceholder")}
              className="mt-1.5 h-10 w-full rounded-md border border-line-1 bg-bg-2 px-3 text-sm focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15"
            />
          </div>
          <Button type="submit">{t("common.search")}</Button>
          {submitted && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setQ("");
                setSubmitted("");
              }}
            >
              {t("common.clear")}
            </Button>
          )}
        </form>
        {submitted && (
          <div className="mt-3">
            <QueryBlock
              query={searchQ}
              empty={(d) => ((d.items ?? []).length === 0 ? <EmptyState title={t("node.noResults")} /> : null)}
            >
              {(d) => (
                <ul className="divide-y divide-line-1">
                  {(d.items ?? []).map((b) => (
                    <li key={b.broker_id}>
                      <NodeRow b={b} depth={0} />
                    </li>
                  ))}
                </ul>
              )}
            </QueryBlock>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title={t("hierarchy.tree", { name: scope.node?.display_name ?? scope.nodeId })}
        actions={
          <div className="w-44">
            <Select
              label={t("common.status")}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={[{ value: "", label: t("common.all") }, ...BROKER_STATUSES.map((s) => ({ value: s, label: t(`brokerStatus.${s}`) }))]}
            />
          </div>
        }
      >
        <Level parentId={scope.nodeId} depth={0} status={status} />
      </SectionCard>

      {creating && (
        <CreateChildDialog nodeId={scope.nodeId} parentName={scope.node?.display_name ?? scope.nodeId} onClose={() => setCreating(false)} />
      )}
    </Guarded>
  );
}

function BreadcrumbLink({ id, name, current }: { id: string; name: string; current: boolean }) {
  const select = useSelectNode();
  if (current)
    return (
      <span aria-current="page" className="font-semibold text-t-1">
        {name}
      </span>
    );
  return (
    <button type="button" className="rounded px-1 hover:text-t-1 hover:underline" onClick={() => select(id)}>
      {name}
    </button>
  );
}

/** One level of the tree: pages of /children, "load more" appends the next page. */
function Level({ parentId, depth, status }: { parentId: string; depth: number; status?: string }) {
  const { t } = useI18n();
  const [pages, setPages] = useState(1);
  return (
    <ul role={depth === 0 ? "tree" : "group"} aria-label={depth === 0 ? t("hierarchy.treeAria") : undefined} className={depth > 0 ? "ml-5 border-l border-line-1 pl-3" : ""}>
      {Array.from({ length: pages }, (_, i) => (
        <LevelPage
          key={`${status ?? ""}-${i}`}
          parentId={parentId}
          depth={depth}
          status={status}
          offset={i * LEVEL_LIMIT}
          last={i === pages - 1}
          onMore={() => setPages((p) => p + 1)}
        />
      ))}
    </ul>
  );
}

function LevelPage({
  parentId,
  depth,
  status,
  offset,
  last,
  onMore,
}: {
  parentId: string;
  depth: number;
  status?: string;
  offset: number;
  last: boolean;
  onMore: () => void;
}) {
  const { t, formatNumber } = useI18n();
  const params = { status: status || undefined, offset, limit: LEVEL_LIMIT };
  const q = useQuery({ queryKey: keys.children(parentId, params), queryFn: () => fetchChildren(parentId, params) });
  if (q.isLoading)
    return (
      <li>
        <LoadingState lines={2} />
      </li>
    );
  if (q.error)
    return (
      <li>
        <ErrorState error={q.error} onRetry={() => void q.refetch()} compact />
      </li>
    );
  const items = q.data?.items ?? [];
  if (offset === 0 && items.length === 0)
    return <li className="py-2 text-sm text-t-3">{depth === 0 ? t("hierarchy.noChildren") : t("hierarchy.noChildrenBelow")}</li>;
  return (
    <>
      {items.map((b) => (
        <TreeItem key={b.broker_id} b={b} depth={depth} />
      ))}
      {last && q.data?.page.has_more && (
        <li className="py-2">
          <Button size="sm" variant="ghost" onClick={onMore}>
            {t("hierarchy.loadMore", {
              shown: formatNumber(offset + items.length),
              total: formatNumber(q.data.page.total),
            })}
          </Button>
        </li>
      )}
    </>
  );
}

function TreeItem({ b, depth }: { b: BrokerItem; depth: number }) {
  const [open, setOpen] = useState(false);
  const expandable = (b.child_count ?? 0) > 0;
  return (
    <li role="treeitem" aria-expanded={expandable ? open : undefined} aria-level={depth + 1}>
      <NodeRow b={b} depth={depth} expandable={expandable} open={open} onToggle={() => setOpen((o) => !o)} />
      {open && expandable && <Level parentId={b.broker_id} depth={depth + 1} />}
    </li>
  );
}

function NodeRow({
  b,
  expandable = false,
  open = false,
  onToggle,
}: {
  b: BrokerItem;
  depth: number;
  expandable?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  const { t, formatNumber } = useI18n();
  const select = useSelectNode();
  const effective = b.effective_status ?? b.status;
  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      {expandable ? (
        <button
          type="button"
          onClick={onToggle}
          className="grid h-6 w-6 place-items-center rounded border border-line-1 text-xs text-t-2 hover:bg-bg-3"
          aria-label={open ? t("hierarchy.collapse", { name: b.display_name }) : t("hierarchy.expand", { name: b.display_name })}
        >
          {open ? "−" : "+"}
        </button>
      ) : (
        <span className="inline-block h-6 w-6" aria-hidden />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-t-1">{b.display_name}</p>
        <p className="mono truncate text-2xs text-t-3">{b.broker_id}</p>
      </div>
      <Badge tone={brokerStatusTone(b.status)}>{t(`brokerStatus.${b.status}`)}</Badge>
      {restrictedByAncestor(b.status, effective) && (
        <Badge tone={brokerStatusTone(effective)} title={t("hierarchy.effectiveHint")}>
          {t("hierarchy.effective", { status: t(`brokerStatus.${effective}`) })}
        </Badge>
      )}
      {typeof b.child_count === "number" && (
        <span className="text-2xs text-t-3">{t("node.childCount", { count: formatNumber(b.child_count) })}</span>
      )}
      <Button size="sm" variant="ghost" onClick={() => select(b.broker_id)}>
        {t("hierarchy.open")}
      </Button>
    </div>
  );
}
